import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Input, Radio, RadioGroup, Stack, Text, VStack } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { urlNavigate } from '../../../helper/utils/navigation';
import { doUpdateWaiver, doUpdateWaiverEmail, doUpdateWaiverPhone } from '../../../redux/waiver/waiver.actions';

export default function AccountDetailsForm() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const { waiver = {}, minor = [], loadingWaiver: isLoading = false } = useSelector((s) => s?.waiver);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            fullname: '',
            birthDate: '',
            email: '',
            phone: '',
            gender: '',
            country: { value: 'ID', label: 'Indonesia' },
            city: { value: '', label: '' },
            address: '',
            childrens: [],
        },
        onSubmit: (values) => {
            const payload = {
                parentID: waiver['id'] || '',
                parentName: values.fullname,
                birthDate: values.birthDate,
                gender: values.gender == 'male' ? '1' : '0',
                city: values?.city?.value || '',
                province: values?.province?.value || '',
                address: values.address,
            };

            dispatch(doUpdateWaiver(payload));
        },
    });

    let today = new Date();
    let eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(today.getFullYear() - 17);
    let dateMax = eighteenYearsAgo.toISOString().split('T')[0];

    const handleChangePhone = (e = '') => {
        formik.setFieldValue('phone', e);
    };

    useEffect(() => {
        if (waiver) {
            formik.setValues({
                fullname: waiver?.fullname,
                birthDate: waiver?.birthDate,
                email: waiver?.email,
                phone: parsePhoneNumber(waiver?.phone ?? '', 'ID').formatInternational(),
                gender: +(waiver?.gender || '1') == 1 ? 'male' : 'female',
                country: { value: 'ID', label: 'Indonesia' },
                city: { value: '', label: '' },
                address: waiver?.address,
            });
        }
    }, [waiver]);

    return (
        <Box as='form' onSubmit={formik.handleSubmit}>
            <Box bg='white' borderRadius='2xl' p={5} py={10} shadow='sm' border='1px' borderColor='gray.100'>
                <VStack spacing={6} align='stretch'>
                    {/* Full Name */}
                    <FormControl isRequired isInvalid={!!formik?.errors.fullname && formik?.touched.fullname}>
                        <FormLabel htmlFor='fullname'>{t('full_name')}</FormLabel>
                        <Input id='fullname' name='fullname' type='text' size='lg' placeholder='Enter your full name' onChange={formik.handleChange} value={formik?.values?.fullname ?? ''} />
                        <FormErrorMessage>{formik?.errors.fullname}</FormErrorMessage>
                    </FormControl>

                    {/* Birth Date */}
                    <FormControl isRequired isInvalid={!!formik?.errors.birthDate && formik?.touched.birthDate}>
                        <FormLabel htmlFor='birthDate'>{t('birth_date')}</FormLabel>
                        <Input id='birthDate' name='birthDate' type='date' size='lg' onChange={formik.handleChange} value={formik?.values?.birthDate ?? ''} max={dateMax} />
                        <FormErrorMessage>{formik?.errors.birthDate}</FormErrorMessage>
                    </FormControl>
                    {/* Gender */}
                    <FormControl isRequired isInvalid={!!formik?.errors.gender && formik?.touched.gender}>
                        <FormLabel htmlFor='gender'>Gender</FormLabel>
                        <RadioGroup id='gender' name='gender' onChange={(value) => formik.setFieldValue('gender', value)} value={formik?.values?.gender ?? ''}>
                            <Stack direction='row' spacing={5}>
                                <Radio value='male' size='lg' colorScheme='primary'>
                                    Male
                                </Radio>
                                <Radio value='female' size='lg' colorScheme='primary'>
                                    Female
                                </Radio>
                            </Stack>
                        </RadioGroup>
                        <FormErrorMessage>{formik?.errors.gender}</FormErrorMessage>
                    </FormControl>
                    {/* Address */}
                    <FormControl isRequired isInvalid={!!formik?.errors.address && formik?.touched.address}>
                        <FormLabel htmlFor='address'>{t('address')}</FormLabel>
                        <Input id='address' name='address' type='text' size='lg' placeholder='Enter your address' onChange={formik.handleChange} value={formik?.values?.address ?? ''} />
                        <FormErrorMessage>{formik?.errors.address}</FormErrorMessage>
                    </FormControl>
                </VStack>
                <Box mt={12}>
                    <Flex w='full' alignItems='center' justifyContent='end' gap={3}>
                        <Button
                            variant='ghost'
                            type='button'
                            color='red.800'
                            size='lg'
                            fontSize='md'
                            borderRadius='lg'
                            fontWeight='semibold'
                            _hover={{
                                bg: 'red.600',
                                color: 'white',
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                            }}
                            transition='all 0.2s'
                            mt={4}
                            onClick={() => navigate(urlNavigate('dashboard'))}>
                            Cancel
                        </Button>
                        <Button
                            type='submit'
                            bg='green.600'
                            color='white'
                            size='lg'
                            fontSize='md'
                            borderRadius='lg'
                            fontWeight='semibold'
                            isLoading={isLoading}
                            _hover={{
                                bg: 'green.900',
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                            }}
                            transition='all 0.2s'
                            mt={4}>
                            Save Changes
                        </Button>
                    </Flex>
                </Box>
            </Box>

            <Box bg='white' borderRadius='2xl' p={5} py={10} shadow='sm' border='1px' borderColor='gray.100' mt={7}>
                <Box>
                    <Heading size='md' color='gray.800' mb={1}>
                        Change Email
                    </Heading>
                    <Text color='gray.600' fontSize='sm'>
                        Change your email address
                    </Text>
                </Box>
                <Box mt={7}>
                    {/* Email */}
                    <FormControl isRequired isInvalid={!!formik?.errors.email && formik?.touched.email}>
                        <FormLabel htmlFor='email'>{t('email')}</FormLabel>
                        <Input id='email' name='email' type='email' size='lg' placeholder='Enter your email' onChange={formik.handleChange} value={formik?.values?.email ?? ''} />
                        <FormErrorMessage>{formik?.errors.email}</FormErrorMessage>
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <Flex w='full' alignItems='center' justifyContent='end' gap={3}>
                        <Button
                            variant='ghost'
                            type='button'
                            color='red.800'
                            size='lg'
                            fontSize='md'
                            borderRadius='lg'
                            fontWeight='semibold'
                            _hover={{
                                bg: 'red.600',
                                color: 'white',
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                            }}
                            transition='all 0.2s'
                            mt={4}
                            onClick={() => navigate(urlNavigate('dashboard'))}>
                            Cancel
                        </Button>
                        <Button
                            type='button'
                            bg='green.600'
                            color='white'
                            size='lg'
                            fontSize='md'
                            borderRadius='lg'
                            fontWeight='semibold'
                            isLoading={isLoading}
                            _hover={{
                                bg: 'green.900',
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                            }}
                            transition='all 0.2s'
                            mt={4}
                            onClick={() => {
                                const payloadMail = {
                                    parentID: waiver['id'] || '',
                                    email: formik.values.email,
                                };

                                dispatch(doUpdateWaiverEmail(payloadMail));
                            }}>
                            Save Changes
                        </Button>
                    </Flex>
                </Box>
            </Box>

            <Box bg='white' borderRadius='2xl' p={5} py={10} shadow='sm' border='1px' borderColor='gray.100' mt={7}>
                <Box>
                    <Heading size='md' color='gray.800' mb={1}>
                        Change Phone
                    </Heading>
                    <Text color='gray.600' fontSize='sm'>
                        Change your phone number
                    </Text>
                </Box>
                <Box mt={5}>
                    {/* Phone Number */}
                    <FormControl isRequired isInvalid={!!formik?.errors.phone && formik?.touched.phone}>
                        <FormLabel htmlFor='phone'>{t('telp')}</FormLabel>
                        <PhoneInput
                            international
                            autoComplete='off'
                            style={{
                                border: '1px solid',
                                height: '3rem',
                                borderRadius: '0.5rem',
                                borderColor: '#e2e8f0',
                                padding: '0 1rem',
                                outline: 'none',
                            }}
                            id='phone'
                            name='phone'
                            defaultCountry='ID'
                            placeholder='8123xxxxxx'
                            hint='8123xxxxxx'
                            value={formik?.values?.phone ?? ''}
                            onChange={handleChangePhone}
                        />
                        <FormErrorMessage>{formik?.errors.phone}</FormErrorMessage>
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <Flex w='full' alignItems='center' justifyContent='end' gap={3}>
                        <Button
                            variant='ghost'
                            type='button'
                            color='red.800'
                            size='lg'
                            fontSize='md'
                            borderRadius='lg'
                            fontWeight='semibold'
                            _hover={{
                                bg: 'red.600',
                                color: 'white',
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                            }}
                            transition='all 0.2s'
                            mt={4}
                            onClick={() => navigate(urlNavigate('dashboard'))}>
                            Cancel
                        </Button>
                        <Button
                            type='button'
                            bg='green.600'
                            color='white'
                            size='lg'
                            fontSize='md'
                            borderRadius='lg'
                            fontWeight='semibold'
                            isLoading={isLoading}
                            _hover={{
                                bg: 'green.900',
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                            }}
                            transition='all 0.2s'
                            mt={4}
                            onClick={() => {
                                const payloadPhone = {
                                    parentID: waiver['id'] || '',
                                    phone: `0${parsePhoneNumber(formik.values?.phone ?? '', 'ID').nationalNumber}`,
                                    dialCode: `+${parsePhoneNumber(formik.values?.phone ?? '', 'ID')?.countryCallingCode ?? ''}`,
                                };

                                dispatch(doUpdateWaiverPhone(payloadPhone));
                            }}>
                            Save Changes
                        </Button>
                    </Flex>
                </Box>
            </Box>
        </Box>
    );
}
